import React from 'react';
import { cn } from '@roger/ui-components';
import { useOpenSupport } from '../../hooks/useSupportContext';

/**
 * Opens the Contact Support modal from inside an error message, pre-filled so
 * the request arrives with the failure already described. Errors that offer
 * support as a next step should all look and behave the same, hence one
 * component rather than a hand-rolled button per surface.
 */
export const ContactSupportLink: React.FC<{
  subject: string;
  hiddenContext?: Record<string, string>;
  className?: string;
  children?: React.ReactNode;
}> = ({ subject, hiddenContext, className, children = 'Contact Support' }) => {
  const openSupport = useOpenSupport();

  return (
    <button
      type='button'
      className={cn(
        'underline underline-offset-2 hover:no-underline',
        className
      )}
      onClick={() => openSupport({ subject, hiddenContext })}
    >
      {children}
    </button>
  );
};

/**
 * Server messages often already tell the user to contact support — the OASIS
 * pre-check failure ends with "contact the Roger Support team", and plenty of
 * others end with "Please contact support." Turn that existing phrase into the
 * link rather than appending a second ask underneath it, which reads as the app
 * repeating itself.
 */
const SUPPORT_MENTION =
  /the Roger Support team|Roger Support team|Roger Support|contact(?:ing)? support/i;

export const SupportMessage: React.FC<{
  message: string;
  subject: string;
  hiddenContext?: Record<string, string>;
}> = ({ message, subject, hiddenContext }) => {
  const match = SUPPORT_MENTION.exec(message);

  if (match) {
    return (
      <>
        {message.slice(0, match.index)}
        <ContactSupportLink subject={subject} hiddenContext={hiddenContext}>
          {match[0]}
        </ContactSupportLink>
        {message.slice(match.index + match[0].length)}
      </>
    );
  }

  // Nothing to link, so offer support as a separate next step.
  return (
    <>
      {message}{' '}
      <ContactSupportLink subject={subject} hiddenContext={hiddenContext} />.
    </>
  );
};
